/* jshint esversion: 6 */
import React from 'react';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import MedicProvider from 'app/redux/Providers/MedicProvider.jsx';
import DateDisplay from 'app/components/common/DateDisplay.jsx';
import Loading from 'app/components/common/Loading.jsx';
import MedicReport from '@/shadcn/components/medic/MedicReport.tsx';
import MedicFeedLink from '@/shadcn/components/medic/MedicFeedLink.tsx';
import MedicEmptyState from '@/shadcn/components/medic/MedicEmptyState.tsx';
import MedicProgress from '@/shadcn/components/medic/MedicProgress.tsx';
import MedicPreviousRuns from '@/shadcn/components/medic/MedicPreviousRuns.tsx';
import {panel as panelCopy} from '@/shadcn/components/medic/copy.ts';
import {Alert, AlertDescription} from '@/shadcn/components/ui/alert';
import {Button} from '@/shadcn/components/ui/button';
import {MD_BUTTON} from '@/shadcn/components/styles.ts';

const POLL_INTERVAL_MS = 1000;

class InputMedicPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            inputId:      this.resolveInputId(props),
            selectedToken: null,
            copied:       false
        };
        this.startRun       = this.startRun.bind(this);
        this.selectRun      = this.selectRun.bind(this);
        this.copyShareLink  = this.copyShareLink.bind(this);
        this.renderRunDate  = this.renderRunDate.bind(this);
        this.pollHandle     = null;
    }

    componentDidMount() {
        const {inputId} = this.state;
        if (inputId) {
            this.props.fetchInputMedicList(inputId);
            this.props.fetchInputMedicStatus(inputId);
        }
    }

    componentDidUpdate(prevProps) {
        const wasInFlight = prevProps.medic.inFlight;
        const nowInFlight = this.props.medic.inFlight;

        if (nowInFlight && !this.pollHandle) {
            this.pollHandle = setInterval(() => {
                this.props.fetchInputMedicStatus(this.state.inputId, this.props.medic.currentRun?.token);
            }, POLL_INTERVAL_MS);
        }
        if (!nowInFlight && this.pollHandle) {
            clearInterval(this.pollHandle);
            this.pollHandle = null;
            if (wasInFlight) {
                this.props.fetchInputMedicList(this.state.inputId);
            }
        }
    }

    componentWillUnmount() {
        if (this.pollHandle) {
            clearInterval(this.pollHandle);
            this.pollHandle = null;
        }
        this.props.resetMedic();
    }

    copyShareLink() {
        const token = this.props.medic.currentRun?.token;
        if (!token) return;
        const url = `${window.location.origin}/medic/share/${token}`;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(() => {
                this.setState({copied: true});
                setTimeout(() => this.setState({copied: false}), 2000);
            });
        }
    }

    /*
     * Passed into the report tree so it stays free of both Redux and
     * DateDisplay — the share page renders the same components with a plain UTC
     * string, having no user timezone to localise against.
     *
     * Renders "3 days ago" with the exact time on hover, because relative is
     * what answers "is this report still current?". Note DateDisplay's own
     * `withRelative` does the inverse — it shows the absolute time and puts the
     * relative string in the title — so it is asked for relative text only and
     * the absolute is supplied as the wrapper's title.
     */
    renderRunDate(run) {
        const {user} = this.props;
        const timezone = user && user.detail && user.detail.timezone;
        if (!timezone) {
            return <span>{run.createdTs}</span>;
        }
        const utc = `${run.createdTs} +0000`;
        return (
            <span title={moment(utc).tz(timezone).format('MMM D, YYYY, h:mm A')}>
                <DateDisplay
                    dateTime={utc}
                    dateTimeFormat="relative"
                    useTimezone={true}
                    user={user}/>
            </span>
        );
    }

    resolveInputId(props) {
        if (props.router && props.router.params && props.router.params.inputId) {
            return parseInt(props.router.params.inputId, 10);
        }
        return null;
    }

    selectRun(token) {
        this.setState({selectedToken: token});
        this.props.fetchInputMedicStatus(this.state.inputId, token);
    }

    startRun() {
        if (!this.state.inputId) return;
        this.props.startInputMedic(this.state.inputId);
    }

    render() {
        const {medic, app} = this.props;
        const {copied} = this.state;
        const run      = medic.currentRun;
        const loading  = app.loading.INPUT_MEDIC_START || (!run && app.loading.INPUT_MEDIC_STATUS);
        const inFlight = medic.inFlight;
        /*
         * Before the first run the empty state carries its own Run button, so the
         * header actions are suppressed rather than offering the same control
         * twice a few hundred pixels apart. They return as soon as there is a
         * report — or a run in flight — to act on.
         */
        const showEmptyState = !run?.result && !loading && !inFlight && !medic.error;

        return (
            /* Same wrapper as AccountPage: .tw-root on the outside, layout on an
               inner div. Matching it exactly is what gives this panel the account
               page's gutter and measure — previously it had py-2 and no horizontal
               padding at all, so the content sat left of the breadcrumb above it.
               text-foreground is the one addition, so the panel's text colour comes
               from the token rather than being inherited from the legacy stylesheet. */
            <div className="tw-root">
            <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:flex-col tw:gap-4 tw:p-4 tw:font-sans tw:text-foreground tw:md:p-6">
                <header className="tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-start tw:md:justify-between">
                    {/* Title and subheadline take account/PageHeading's exact
                        scale, weight and leading. h3 rather than h1 because this
                        is a panel within a page that already has its heading —
                        the level is document structure, the size is presentation. */}
                    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
                        <h3 className="tw:text-2xl tw:leading-8 tw:font-semibold tw:text-foreground">
                            {panelCopy.title}
                        </h3>
                        <MedicFeedLink feedUrl={run?.result?.feedUrl}/>
                    </div>
                    {!showEmptyState && (
                        <div className="tw:flex tw:shrink-0 tw:gap-2">
                            <Button type="button" className={MD_BUTTON} onClick={this.startRun} disabled={inFlight}>
                                {inFlight ? panelCopy.runBusy : panelCopy.runIdle}
                            </Button>
                            {run?.token && run.status === 'done' && (
                                <Button type="button" variant="outline" className={MD_BUTTON} onClick={this.copyShareLink}>
                                    {copied ? panelCopy.shareCopied : panelCopy.shareIdle}
                                </Button>
                            )}
                        </div>
                    )}
                </header>

                {inFlight && run?.message && (
                    <Alert>
                        <AlertDescription>{run.message}</AlertDescription>
                    </Alert>
                )}

                {medic.error && (
                    <Alert variant="destructive">
                        <AlertDescription>{medic.error}</AlertDescription>
                    </Alert>
                )}

                {inFlight && <MedicProgress progress={run?.progress}/>}

                {loading && !run && (<Loading/>)}

                {/* Before the first run there is no report, no feed URL and no
                    previous-runs list, so without this the panel is a title and a
                    button. Suppressed while a run is loading or in flight, and
                    when an error banner is already explaining the empty space. */}
                {showEmptyState && <MedicEmptyState onRun={this.startRun} busy={inFlight}/>}

                {/* runState is passed only when the progress bar above renders
                    nothing, which is when the report's summary card is otherwise
                    an empty frame holding a timestamp. The worker writes its first
                    progress as it picks a run up, so no progress on an in-flight
                    run means it has not started — a state worth naming, since it
                    is also what a stuck run looks like. */}
                {run?.result && (
                    <MedicReport
                        result={run.result}
                        renderRanAt={run.createdTs ? () => this.renderRunDate(run) : undefined}
                        runState={inFlight && !run.progress
                            ? (run.status === 'running' ? 'running' : 'queued')
                            : undefined}/>
                )}

                <MedicPreviousRuns
                    runs={medic.list}
                    currentToken={run?.token}
                    onSelect={this.selectRun}
                    renderWhen={this.renderRunDate}/>
            </div>
            </div>
        );
    }
}

InputMedicPanel.displayName = "InputMedicPanel";

export default ComponentWithContext(MedicProvider(InputMedicPanel));
